/**
 * Compliance Audit page — admin pane-of-glass for the regulated-content programme.
 *
 * Shows: pending approvals count, expired/expiring assets, recent overrides,
 * a table of expiring artifacts in the next 30 days, and the audit-log slice
 * of compliance events (mode changes, settings updates, override events,
 * approval decisions).
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Loader2, AlertTriangle, Clock, ShieldCheck } from 'lucide-react';
import { contentComplianceAPI, ComplianceDashboard, ExpiringArtifact } from '@/services/api/contentCompliance';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useComplianceMode } from '@/hooks/useComplianceMode';

interface Props {
  /** When set, renders a back link instead of standalone-page header. */
  onBack?: () => void;
  /** Override the org id resolved from context (used when embedded in Org Settings). */
  organizationId?: number;
}

export default function ComplianceAuditPage({ onBack, organizationId }: Props = {}) {
  const { selectedOrganization } = useOrganization();
  const { canManageCompliance } = useComplianceMode();
  const effectiveOrgId = organizationId ?? selectedOrganization?.id ?? null;
  const [dashboard, setDashboard] = useState<ComplianceDashboard | null>(null);
  const [expiring, setExpiring] = useState<{ media_assets: ExpiringArtifact[]; documents: ExpiringArtifact[] } | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!effectiveOrgId) {
      // No org context yet; stop the spinner and let the empty-state render.
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    Promise.all([
      contentComplianceAPI.getDashboard(effectiveOrgId),
      contentComplianceAPI.listExpiring(effectiveOrgId, 30),
      contentComplianceAPI.listEnforcementEvents(effectiveOrgId, 100),
    ])
      .then(([d, e, ev]) => {
        setDashboard(d);
        setExpiring({ media_assets: e.media_assets, documents: e.documents });
        setEvents(ev);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Compliance audit load failed:', err);
        setLoadError(err?.response?.data?.detail || err?.message || 'Failed to load compliance audit');
      })
      .finally(() => setLoading(false));
  }, [effectiveOrgId]);

  if (!onBack && !canManageCompliance) {
    return (
      <div className="container mx-auto p-8 max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            You don't have permission to view the compliance audit page.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-10 text-center text-sm text-ink-500">
        <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading compliance audit…
      </div>
    );
  }

  if (loadError || !dashboard) {
    return (
      <div className={onBack ? 'space-y-4' : 'container mx-auto py-8 px-4 max-w-3xl'}>
        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-700 mb-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Compliance Settings
          </button>
        )}
        <Card>
          <CardContent className="py-8 text-center space-y-2">
            <AlertTriangle className="h-6 w-6 text-amber-500 inline" />
            <div className="text-sm font-medium">Couldn't load the compliance audit</div>
            <div className="text-xs text-ink-500">{loadError || 'No data returned'}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={onBack ? 'space-y-6' : 'container mx-auto py-8 px-4 max-w-7xl space-y-6'}>
      {onBack && (
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-700 mb-2 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Compliance Settings
        </button>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Compliance Audit</h1>
          <p className="text-sm text-ink-500">
            {selectedOrganization?.name} ·{' '}
            <Badge className={dashboard.mode === 'regulated' ? 'bg-amber-100 text-amber-900' : ''}>
              {dashboard.mode === 'regulated' ? 'Regulated entity' : 'Standard mode'}
            </Badge>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label="Pending approvals"
          value={dashboard.pending_approvals}
          icon={<ShieldCheck className="h-4 w-4 text-amber-600" />}
        />
        <Stat
          label="Expired assets"
          value={dashboard.expired_assets}
          icon={<AlertTriangle className="h-4 w-4 text-red-600" />}
          tone={dashboard.expired_assets > 0 ? 'red' : undefined}
        />
        <Stat
          label="Expiring (30d)"
          value={dashboard.expiring_soon}
          icon={<Clock className="h-4 w-4 text-amber-500" />}
          tone={dashboard.expiring_soon > 0 ? 'amber' : undefined}
        />
        <Stat
          label="Overrides (30d)"
          value={dashboard.overrides_30d}
          icon={<ShieldCheck className="h-4 w-4 text-blue-600" />}
        />
      </div>

      <Tabs defaultValue="expiring">
        <TabsList>
          <TabsTrigger value="expiring">Expiring artifacts</TabsTrigger>
          <TabsTrigger value="events">Enforcement events</TabsTrigger>
        </TabsList>

        <TabsContent value="expiring" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Media assets expiring within 30 days</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ArtifactTable rows={expiring?.media_assets || []} kind="media" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Member documents expiring within 30 days</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ArtifactTable rows={expiring?.documents || []} kind="document" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent compliance enforcement events</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-paper text-left">
                  <tr>
                    <th className="p-3">When</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Action</th>
                    <th className="p-3">By</th>
                    <th className="p-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-ink-500">
                        No compliance events recorded yet.
                      </td>
                    </tr>
                  ) : (
                    events.map((e) => (
                      <tr key={e.id} className="border-t">
                        <td className="p-3 whitespace-nowrap">{new Date(e.created_ts).toLocaleString()}</td>
                        <td className="p-3">
                          <Badge variant="outline">{e.entity_type}</Badge>
                        </td>
                        <td className="p-3">{e.action}</td>
                        <td className="p-3">{e.performed_by_name || `User #${e.performed_by || ''}`}</td>
                        <td className="p-3 text-ink-500">{e.notes || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: 'red' | 'amber';
}) {
  const toneClass =
    tone === 'red'
      ? 'border-red-200'
      : tone === 'amber'
      ? 'border-amber-200'
      : '';
  return (
    <Card className={toneClass}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-ink-500">{label}</span>
          {icon}
        </div>
        <div className="font-display text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function ArtifactTable({ rows, kind }: { rows: ExpiringArtifact[]; kind: 'media' | 'document' }) {
  if (rows.length === 0) {
    return (
      <div className="py-6 text-center text-ink-500 text-sm">
        Nothing {kind === 'media' ? 'in the media library' : 'across member documents'} is expiring in this window.
      </div>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-paper text-left">
        <tr>
          <th className="p-3">Name / ID</th>
          <th className="p-3">Type</th>
          <th className="p-3">Expires</th>
          <th className="p-3">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t">
            <td className="p-3">{r.name || `#${r.id}`}</td>
            <td className="p-3">
              {r.asset_type || (r as any).target_type}
              {r.regulated_category ? <span className="text-ink-400"> · {r.regulated_category}</span> : null}
            </td>
            <td className="p-3">{r.expiration_date}</td>
            <td className="p-3">
              <Badge
                className={
                  r.compliance_status === 'expired' || (r as any).verification_status === 'expired'
                    ? 'bg-red-100 text-red-900'
                    : 'bg-amber-100 text-amber-900'
                }
              >
                {r.compliance_status || (r as any).verification_status || 'expiring_soon'}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
